import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import confetti from 'canvas-confetti';

const LOGO = "/assets/logo-puro.png";
const LOGO_FILTER = { filter: 'brightness(0) saturate(100%) invert(45%) sepia(28%) saturate(520%) hue-rotate(357deg) brightness(0.92)' };

function fireGoldConfetti() {
  const colors = ['#C99A55', '#E8D9A0', '#F5EFE5', '#D4B862'];
  confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 }, colors, gravity: 0.8, scalar: 1.1, ticks: 150 });
  setTimeout(() => confetti({ particleCount: 40, spread: 90, origin: { y: 0.5 }, colors, gravity: 0.6, scalar: 0.9, ticks: 120 }), 300);
}

/* === WELCOME SPLASH (first visit) === */
export function WelcomeSplash() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    const seen = sessionStorage.getItem('puro_welcomed');
    if (!seen) {
      setShow(true);
      sessionStorage.setItem('puro_welcomed', 'true');
      setTimeout(() => setShow(false), 3000);
    }
  }, []);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
          className="fixed inset-0 z-[99999] bg-[#F5EFE5] flex items-center justify-center"
        >
          <motion.div className="text-center" initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}>
            <motion.img
              src={LOGO}
              alt="PURO"
              className="h-24 w-auto mx-auto mb-6"
              style={LOGO_FILTER}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.7 }}
            />
            <motion.p
              className="font-heading text-2xl text-[#493329] font-medium italic"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.6 }}
            >
              Bienvenue
            </motion.p>
            <motion.div
              className="w-12 h-0.5 bg-[#C99A55] mx-auto mt-4"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ delay: 0.9, duration: 0.5 }}
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* === REGISTER CELEBRATION === */
export function RegisterCelebration({ name, isPrivilege, onComplete }) {
  useEffect(() => {
    const timer = setTimeout(() => fireGoldConfetti(), 400);
    return () => clearTimeout(timer);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="fixed inset-0 z-[99999] bg-[#F5EFE5] flex items-center justify-center px-4"
    >
      <motion.div className="text-center max-w-md">
        <motion.img
          src={LOGO}
          alt="PURO"
          className="h-20 w-auto mx-auto mb-6"
          style={LOGO_FILTER}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
        />

        {isPrivilege ? (
          <>
            <motion.div
              className="inline-block bg-[#C99A55] text-white text-xs uppercase tracking-widest px-4 py-1.5 rounded-full mb-4 font-semibold"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
            >
              Membre Privilège
            </motion.div>
            <motion.h1
              className="font-heading font-medium text-3xl sm:text-4xl text-[#493329] tracking-tight mb-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              Merci {name ? name.split(' ')[0] : ''}
            </motion.h1>
            <motion.p
              className="text-[#7A6455] mb-2"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.8 }}
            >
              Votre confiance nous honore.
            </motion.p>
            <motion.p
              className="text-[#C99A55] font-heading text-lg font-medium mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
            >
              -20% sur votre première année
            </motion.p>
          </>
        ) : (
          <>
            <motion.h1
              className="font-heading font-medium text-3xl sm:text-4xl text-[#493329] tracking-tight mb-3"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.6 }}
            >
              Bienvenue {name ? name.split(' ')[0] : ''} !
            </motion.h1>
            <motion.p
              className="text-[#7A6455] mb-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              Vous faites désormais partie de l'univers PURO.
            </motion.p>
          </>
        )}

        <motion.div
          className="w-12 h-0.5 bg-[#C99A55] mx-auto mb-6"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 1.2, duration: 0.5 }}
        />

        <motion.button
          onClick={onComplete}
          className="bg-[#C99A55] hover:bg-[#493329] text-white rounded-full px-8 py-3 font-medium transition-all duration-400 btn-premium"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.4, duration: 0.5 }}
        >
          Découvrir mon espace
        </motion.button>
      </motion.div>
    </motion.div>
  );
}

/* === LOGIN WELCOME BACK === */
export function LoginWelcomeBack({ name, isPrivilege, onComplete }) {
  useEffect(() => {
    const timer = setTimeout(() => onComplete(), 2500);
    return () => clearTimeout(timer);
  }, [onComplete]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="fixed inset-0 z-[99999] bg-[#F5EFE5] flex items-center justify-center"
      onClick={onComplete}
    >
      <motion.div className="text-center">
        <motion.img
          src={LOGO}
          alt="PURO"
          className="h-16 w-auto mx-auto mb-5"
          style={LOGO_FILTER}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        />
        <motion.h2
          className="font-heading font-medium text-2xl sm:text-3xl text-[#493329] tracking-tight"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          Ravie de vous retrouver{name ? `, ${name.split(' ')[0]}` : ''}
        </motion.h2>
        {isPrivilege && (
          <motion.span
            className="inline-block mt-3 bg-[#C99A55]/10 text-[#C99A55] text-xs uppercase tracking-widest px-3 py-1 rounded-full font-semibold"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
          >
            Membre Privilège
          </motion.span>
        )}
        <motion.div
          className="w-12 h-0.5 bg-[#C99A55] mx-auto mt-4"
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.5, duration: 0.4 }}
        />
      </motion.div>
    </motion.div>
  );
}
